/**
 * MigrationService - Data format migration for patch data
 * 
 * Handles migration of device and connection data between format versions.
 * All functions are pure - they take data in and return transformed data out.
 */

/**
 * Migrate a device from old format (v2) to new format (v3 with portGroups)
 * 
 * @param {Object} oldDevice - Device in old format (audioInputs, midiOutputs, etc.)
 * @returns {Object} Device in new format (with portGroups array)
 */
export function migrateDevice(oldDevice) {
    // If already has portGroups, it's new format (but ensure manufacturer/version exist)
    if (oldDevice.portGroups) {
        return {
            ...oldDevice,
            manufacturer: oldDevice.manufacturer || '',
            version: oldDevice.version || ''
        };
    }
    
    // Convert old format to new (groups identified by index, no UUID needed)
    const portGroups = [];
    
    if (oldDevice.audioInputs > 0) {
        portGroups.push({
            name: 'Audio In',
            direction: 'input',
            type: 'audio',
            count: oldDevice.audioInputs
        });
    }
    if (oldDevice.audioOutputs > 0) {
        portGroups.push({
            name: 'Audio Out',
            direction: 'output',
            type: 'audio',
            count: oldDevice.audioOutputs
        });
    }
    if (oldDevice.midiInputs > 0) {
        portGroups.push({
            name: 'MIDI In',
            direction: 'input',
            type: 'midi',
            count: oldDevice.midiInputs
        });
    }
    if (oldDevice.midiOutputs > 0) {
        portGroups.push({
            name: 'MIDI Out',
            direction: 'output',
            type: 'midi',
            count: oldDevice.midiOutputs
        });
    }
    if (oldDevice.cvInputs > 0) {
        portGroups.push({
            name: 'CV In',
            direction: 'input',
            type: 'cv',
            count: oldDevice.cvInputs
        });
    }
    if (oldDevice.cvOutputs > 0) {
        portGroups.push({
            name: 'CV Out',
            direction: 'output',
            type: 'cv',
            count: oldDevice.cvOutputs
        });
    }
    if (oldDevice.gateInputs > 0) {
        portGroups.push({
            name: 'Gate In',
            direction: 'input',
            type: 'gate',
            count: oldDevice.gateInputs
        });
    }
    if (oldDevice.gateOutputs > 0) {
        portGroups.push({
            name: 'Gate Out',
            direction: 'output',
            type: 'gate',
            count: oldDevice.gateOutputs
        });
    }
    
    return {
        id: oldDevice.id,
        name: oldDevice.name,
        manufacturer: oldDevice.manufacturer || '',
        version: oldDevice.version || '',
        portGroups,
        position: oldDevice.position
    };
}

/**
 * Migrate connections from old format to new index-based format
 * 
 * Handles three formats:
 * 1. Newest: already has sourceGroupIndex
 * 2. Middle: has sourceGroupId (UUID)
 * 3. Oldest: type-based (no group ID at all)
 * 
 * @param {Array} connections - Array of connections in any format
 * @param {Object} deviceMap - Map of deviceId -> migrated device (with portGroups)
 * @returns {Array} Connections in newest format (with sourceGroupIndex/targetGroupIndex)
 */
export function migrateConnections(connections, deviceMap) {
    return connections.map(conn => {
        // If already has sourceGroupIndex, it's the newest format
        if (conn.sourceGroupIndex !== undefined) {
            return conn;
        }
        
        // If has sourceGroupId (UUID), convert to index
        if (conn.sourceGroupId) {
            const sourceDevice = deviceMap[conn.sourceDeviceId];
            const targetDevice = deviceMap[conn.targetDeviceId];
            
            if (!sourceDevice || !targetDevice) return null;
            
            // Find group indices by matching UUID
            const srcIdx = sourceDevice.portGroups.findIndex(g => g.id === conn.sourceGroupId);
            const tgtIdx = targetDevice.portGroups.findIndex(g => g.id === conn.targetGroupId);
            
            return {
                id: conn.id,
                sourceDeviceId: conn.sourceDeviceId,
                sourceGroupIndex: srcIdx >= 0 ? srcIdx : 0,
                sourcePortType: conn.sourcePortType,
                sourcePortIndex: conn.sourcePortIndex,
                targetDeviceId: conn.targetDeviceId,
                targetGroupIndex: tgtIdx >= 0 ? tgtIdx : 0,
                targetPortType: conn.targetPortType,
                targetPortIndex: conn.targetPortIndex
            };
        }
        
        // Oldest format: type-based connections (no group ID at all)
        const sourceDevice = deviceMap[conn.sourceDeviceId];
        const targetDevice = deviceMap[conn.targetDeviceId];
        
        if (!sourceDevice || !targetDevice) return null;
        
        const sourceGroupIdx = sourceDevice.portGroups.findIndex(g => 
            g.type === conn.sourcePortType && g.direction === 'output'
        );
        const targetGroupIdx = targetDevice.portGroups.findIndex(g => 
            g.type === conn.targetPortType && g.direction === 'input'
        );
        
        if (sourceGroupIdx < 0 || targetGroupIdx < 0) return null;
        
        return {
            id: conn.id,
            sourceDeviceId: conn.sourceDeviceId,
            sourceGroupIndex: sourceGroupIdx,
            sourcePortType: conn.sourcePortType,
            sourcePortIndex: conn.sourcePortIndex,
            targetDeviceId: conn.targetDeviceId,
            targetGroupIndex: targetGroupIdx,
            targetPortType: conn.targetPortType,
            targetPortIndex: conn.targetPortIndex
        };
    }).filter(c => c !== null);
}
